import type RootState from 'app-components/utils/redux-store/rootState';

import { getTransactionActionCapabilityDefinition } from './capabilities';
import { executeStructuredModelRequest } from './executeStructuredModelRequest';
import { transactionActionExecutionProfiles } from './executionProfiles';
import type { CleanupTransactionProposal } from './features/cleanup-transaction';
import type { RecategorizeProposal } from './features/recategorize';
import { buildTransactionActionLaunch } from './launchTransactionAction';
import type {
  LaunchTransactionActionArgs,
  RenamePayeeProposal,
  TransactionActionBatch,
  TransactionActionExecutionResult,
} from './proposalTypes';

// Framework step 5 in docs/features/transaction-actions/README.md ("Execution lifecycle"): owns
// timeout/retry policy and turns every capability's structured-model runner request into one of
// the shared TransactionActionExecutionResult statuses.

// Safety net for the cast in buildProposalsResult below: capability registration is what's
// actually supposed to keep a capability's parseStructuredResponse paired with its proposal
// shape, but nothing at the type level enforces that pairing. Checking a field unique to each
// proposal shape here means a future capability with a mismatched parser fails loudly instead of
// silently mislabeling its proposals under the wrong capability.
const proposalMatchesCapability = (
  capability: TransactionActionBatch['capability'],
  proposal: RecategorizeProposal | RenamePayeeProposal | CleanupTransactionProposal,
): boolean => {
  switch (capability) {
    case 'recategorize':
      return 'proposedCategoryId' in proposal;
    case 'rename_payee':
      return 'proposedPayee' in proposal;
    case 'cleanup_transaction':
      return 'suggestions' in proposal;
    default:
      return false;
  }
};

export const executeTransactionActionRequest = async (
  state: RootState,
  args: LaunchTransactionActionArgs,
): Promise<TransactionActionExecutionResult> => {
  const { batch, runnerOutput } = buildTransactionActionLaunch(state, args);

  if (!runnerOutput) {
    return { status: 'no_eligible_transactions', batch };
  }

  const profile = transactionActionExecutionProfiles[batch.capability];
  const eligibleTransactions = batch.eligibleTransactions;
  const capabilityDefinition = getTransactionActionCapabilityDefinition(batch.capability);

  if (!capabilityDefinition.parseStructuredResponse) {
    return { status: 'request_failed', batch, error: `Unsupported structured model capability: ${batch.capability}` };
  }
  const parseStructuredResponse = capabilityDefinition.parseStructuredResponse;

  // batch.capability and parseStructuredResponse are paired by capability registration
  // (getTransactionActionCapabilityDefinition), which is how the proposals shape actually
  // stays correlated with the batch type - the type system can't express that pairing
  // through a plain function call, hence the cast. proposalMatchesCapability guards it at runtime.
  const buildProposalsResult = (
    proposals: RecategorizeProposal[] | RenamePayeeProposal[] | CleanupTransactionProposal[],
  ) => {
    const [firstProposal] = proposals;
    if (firstProposal && !proposalMatchesCapability(batch.capability, firstProposal)) {
      throw new Error(`Transaction action proposal shape does not match capability "${batch.capability}"`);
    }
    return { status: 'proposals', batch, proposals } as TransactionActionExecutionResult;
  };

  const result = await executeStructuredModelRequest({
    request: runnerOutput.request,
    profile,
    capability: batch.capability,
    parseResponse: (assistantResponse) => parseStructuredResponse(eligibleTransactions, assistantResponse),
  });

  return result.status === 'success'
    ? buildProposalsResult(result.data)
    : { status: 'request_failed', batch, error: result.error };
};
